import { create } from 'zustand';
import axiosInstance from '../api/api';
import { API_ROUTES } from '../config/api-routes';
import type { CandidateAnalytics } from '../types/candidateAnalytics';

const ANALYTICS_CACHE_TTL_MS = 15_000;

type FetchOptions = {
  force?: boolean;
};

interface AnalyticsState {
  candidateAnalytics: CandidateAnalytics | null;
  loadingCandidateAnalytics: boolean;
  candidateAnalyticsLastFetchedAt: number | null;
  candidateAnalyticsError: string | null;

  fetchCandidateAnalytics: (opts?: FetchOptions) => Promise<CandidateAnalytics>;
  clearAnalyticsCache: () => void;
}

let candidateAnalyticsInFlight: Promise<CandidateAnalytics> | null = null;

function isFresh(lastFetchedAt: number | null) {
  if (!lastFetchedAt) return false;
  return Date.now() - lastFetchedAt < ANALYTICS_CACHE_TTL_MS;
}

export const useAnalyticsStore = create<AnalyticsState>((set, get) => ({
  candidateAnalytics: null,
  loadingCandidateAnalytics: false,
  candidateAnalyticsLastFetchedAt: null,
  candidateAnalyticsError: null,

  fetchCandidateAnalytics: async (opts) => {
    const state = get();
    if (
      !opts?.force &&
      state.candidateAnalytics &&
      isFresh(state.candidateAnalyticsLastFetchedAt)
    ) {
      return state.candidateAnalytics;
    }

    if (!opts?.force && candidateAnalyticsInFlight) {
      if (!get().loadingCandidateAnalytics) {
        set({ loadingCandidateAnalytics: true });
      }
      return candidateAnalyticsInFlight;
    }

    candidateAnalyticsInFlight = (async () => {
      set({
        loadingCandidateAnalytics: true,
        candidateAnalyticsError: null,
      });
      try {
        const res = await axiosInstance.get(API_ROUTES.ANALYTICS.CANDIDATE_SNAPSHOT);
        const payload = (res.data?.data ?? res.data) as CandidateAnalytics;
        set({
          candidateAnalytics: payload,
          loadingCandidateAnalytics: false,
          candidateAnalyticsLastFetchedAt: Date.now(),
          candidateAnalyticsError: null,
        });
        return payload;
      } catch (err: unknown) {
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Failed to load analytics';
        set({
          loadingCandidateAnalytics: false,
          candidateAnalyticsError: msg,
        });
        throw err;
      } finally {
        candidateAnalyticsInFlight = null;
      }
    })();

    return candidateAnalyticsInFlight;
  },

  clearAnalyticsCache: () => {
    candidateAnalyticsInFlight = null;
    set({
      candidateAnalytics: null,
      loadingCandidateAnalytics: false,
      candidateAnalyticsLastFetchedAt: null,
      candidateAnalyticsError: null,
    });
  },
}));
